/**
 * Coverage-by-dimension summary for the wrap-up (digest review + completion).
 *
 * Adi's model: "completed = all dimensions addressed; wrapped up early = paused,
 * come back to finish." This panel makes the gap visible — it foregrounds the
 * dimensions STILL TO COVER (the reason to return) and lists what's already
 * captured. Driven ONLY by the server's `dimensionCoverage` (founder-friendly
 * label + 3-state marker) — trade-secret-safe by construction (no numbers,
 * scores, weights, or counts). Reuses the same helpers/tones as the in-chat
 * `InterviewProgress` coverage panel so the visual language matches.
 */

import React, { memo, useMemo } from 'react';
import { View, Text } from 'react-native';
import { Check, Minus } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import type {
  DimensionCoverage,
  DimensionCoverageState,
} from '@/lib/interview/types';
import { coverageStateMeta, visibleDimensionCoverage } from './interview-coverage';

type Colors = ReturnType<typeof useThemeColors>;

function chipTone(state: DimensionCoverageState, colors: Colors): { bg: string; fg: string } {
  switch (state) {
    case 'captured':
      return { bg: colors.traffic.greenLight, fg: colors.success };
    case 'light':
      return { bg: colors.amber.light, fg: colors.amber.main };
    case 'not_yet':
    default:
      return { bg: colors.surface.tertiary, fg: colors.text.tertiary };
  }
}

const Chip = memo(function Chip({
  label,
  state,
  colors,
}: {
  label: string;
  state: DimensionCoverageState;
  colors: Colors;
}) {
  const { bg, fg } = chipTone(state, colors);
  const { statusLabel } = coverageStateMeta(state);
  return (
    <View
      className="flex-row items-center rounded-full px-3 py-1.5"
      style={{ maxWidth: isWeb ? 260 : 200, backgroundColor: bg }}
    >
      {state === 'captured' ? (
        <Check size={12} color={fg} strokeWidth={3} />
      ) : (
        <Minus size={12} color={fg} strokeWidth={3} />
      )}
      <Text numberOfLines={1} className="text-[11px] font-semibold ml-1.5 flex-shrink" style={{ color: fg }}>
        {label}
      </Text>
      <Text numberOfLines={1} className="text-[11px] ml-1.5" style={{ color: fg, opacity: 0.75 }}>
        {'·'} {statusLabel}
      </Text>
    </View>
  );
});

export interface CoverageByDimensionProps {
  dimensionCoverage?: DimensionCoverage[] | null;
}

export const CoverageByDimension = memo(function CoverageByDimension({
  dimensionCoverage,
}: CoverageByDimensionProps) {
  const colors = useThemeColors();
  const rows = useMemo(() => visibleDimensionCoverage(dimensionCoverage), [dimensionCoverage]);

  const stillToCover = rows.filter((r) => r.state === 'not_yet');
  const covered = rows.filter((r) => r.state !== 'not_yet');

  if (rows.length === 0) return null;

  const allCovered = stillToCover.length === 0;

  return (
    <View>
      <Text className="text-xs font-bold tracking-wide mb-1" style={{ color: colors.text.tertiary }}>
        COVERAGE BY AREA
      </Text>
      <Text className="text-[13px] mb-3" style={{ color: colors.text.secondary }}>
        {allCovered
          ? "You've touched on every area — nice and thorough."
          : `${stillToCover.length} ${stillToCover.length === 1 ? 'area' : 'areas'} still to cover. You can come back anytime to strengthen your score.`}
      </Text>

      {stillToCover.length > 0 && (
        <View className="mb-3">
          <Text className="text-[11px] font-semibold mb-1.5" style={{ color: colors.text.tertiary }}>
            Still to cover
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {stillToCover.map((r, i) => (
              <Chip key={`ntc-${r.label}-${i}`} label={r.label} state={r.state} colors={colors} />
            ))}
          </View>
        </View>
      )}

      {covered.length > 0 && (
        <View>
          <Text className="text-[11px] font-semibold mb-1.5" style={{ color: colors.text.tertiary }}>
            Covered
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {covered.map((r, i) => (
              <Chip key={`cov-${r.label}-${i}`} label={r.label} state={r.state} colors={colors} />
            ))}
          </View>
        </View>
      )}
    </View>
  );
});
